import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, TablePagination, TextField, InputAdornment, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert, LinearProgress, Tooltip, Paper, Grid,
  FormControl, InputLabel, MenuItem, Select, FormHelperText
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { baseUrl, config, requestID } from "../../common/utils";
import { IonContent, IonPage } from '@ionic/react';

interface Course {
  id: number;
  cou_id: string;
  cou_name: string;
  branch_id: number;
  business_id: number;
  created_by: number;
  created_at?: string;
  updated_at?: string;
}

interface CourseFormData {
  id?: number;
  cou_id: string;
  cou_name: string;
  branch_id: number;
  business_id: number;
  created_by?: number;
}

const CbtCourses: React.FC = () => {
  // State Management
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view'>('create');
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);

  // Form State
  const [formData, setFormData] = useState<CourseFormData>({
    cou_id: '',
    cou_name: '',
    branch_id: 0,
    business_id: 0
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Notification States
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // ================= API CALLS =================

  // Fetch all courses
  const fetchCourses = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/index`, formData, config);
      
      if (response.data.status === true) {
        setCourses(response.data.data || []);
      } else {
        showSnackbar('Failed to fetch courses', 'error');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create course
  const createCourse = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const formDatax = new FormData();
      formDatax.append("requestID", requestID());
      formDatax.append("cou_id", formData.cou_id);
      formDatax.append("cou_name", formData.cou_name);

      const response = await axios.post(`${baseUrl()}Cbt_Courses/create`, formDatax, config);
      
      if (response.data.status === true) {
        showSnackbar('Course created successfully!', 'success');
        fetchCourses();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to create course', 'error');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update course
  const updateCourse = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const updateData = new FormData();
      updateData.append("requestID", requestID());
      updateData.append("id", currentCourse?.id.toString() || '');
      updateData.append("cou_id", formData.cou_id);
      updateData.append("cou_name", formData.cou_name);

      const response = await axios.post(`${baseUrl()}Cbt_Courses/update`, updateData, config);
      
      if (response.data.status === true) {
        showSnackbar('Course updated successfully!', 'success');
        fetchCourses();
        handleCloseDialog();
      } else {
        showSnackbar(response.data.message || 'Failed to update course', 'error');
      }
    } catch (error) {
      console.error('Error updating course:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete course
  const deleteCourse = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/delete`, formData, config);
      
      if (response.data.status === true) {
        showSnackbar('Course deleted successfully!', 'success');
        fetchCourses();
      } else {
        showSnackbar(response.data.message || 'Failed to delete course', 'error');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // View course details
  const viewCourse = async (id: number) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("id", id.toString());

      const response = await axios.post(`${baseUrl()}Cbt_Courses/show`, formData, config);
      
      if (response.data.status === true && response.data.data) {
        setCurrentCourse(response.data.data);
        setFormData({
          cou_id: response.data.data.cou_id,
          cou_name: response.data.data.cou_name,
          branch_id: response.data.data.branch_id,
          business_id: response.data.data.business_id
        });
        setDialogType('view');
        setOpenDialog(true);
      } else {
        showSnackbar('Course not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ================= HELPER FUNCTIONS =================

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    if (!formData.cou_name.trim()) {
      errors.cou_name = 'Course name is required';
    } else if (formData.cou_name.length < 3) {
      errors.cou_name = 'Course name must be at least 3 characters';
    }

    if (!formData.cou_id.trim()) {
      errors.cou_id = 'Course ID is required';
    }

    return errors;
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (type: 'create' | 'edit' | 'view', course?: Course) => {
    setDialogType(type);
    
    if (course) {
      setCurrentCourse(course);
      setFormData({
        id: course.id,
        cou_id: course.cou_id,
        cou_name: course.cou_name,
        branch_id: course.branch_id,
        business_id: course.business_id
      });
    } else {
      // Generate new course ID for creation
      const newCouId = `cou_id_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      setFormData({
        cou_id: newCouId,
        cou_name: '',
        branch_id: 0,
        business_id: 0
      });
      setCurrentCourse(null);
    }
    
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCourse(null);
    setFormErrors({});
  };

  const handleSubmit = () => {
    if (dialogType === 'create') {
      createCourse();
    } else if (dialogType === 'edit') {
      updateCourse();
    }
  };

  const handleFormChange = (field: keyof CourseFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Filter courses based on search
  const filteredCourses = courses.filter(course =>
    course.cou_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.cou_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const visibleCourses = filteredCourses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Initial fetch
  useEffect(() => {
    fetchCourses();
  }, []);

  // ================= RENDER =================

  return (
    <IonPage>
      <IonContent>
 <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card sx={{ 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        mb: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Box sx={{ p: 3, color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon fontSize="large" />
                CBT Courses Management
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                Manage all your CBT courses in one place
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
              sx={{
                bgcolor: 'white',
                color: '#764ba2',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              New Course
            </Button>
          </Box>
          
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search courses by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'white' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&::placeholder': { color: 'rgba(255,255,255,0.7)' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchCourses}
                disabled={loading}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Loading Indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Courses Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, width: '15%' }}>COURSE ID</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '40%' }}>COURSE NAME</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '20%' }}>CREATED</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '25%' }} align="center">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCourses.length > 0 ? (
                visibleCourses.map((course) => (
                  <TableRow key={course.id} hover>
                    <TableCell>
                      <Chip
                        label={course.cou_id}
                        size="small"
                        sx={{
                          bgcolor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {course.cou_name}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      {course.created_at ? new Date(course.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => viewCourse(course.id)}
                            sx={{ color: '#3b82f6' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Course">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', course)}
                            sx={{ color: '#10b981' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Course">
                          <IconButton
                            size="small"
                            onClick={() => deleteCourse(course.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No courses found. {searchTerm && 'Try a different search term.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredCourses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Course Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography variant="h6">
              {dialogType === 'create' && 'Create New Course'}
              {dialogType === 'edit' && 'Edit Course'}
              {dialogType === 'view' && 'Course Details'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course ID"
                value={formData.cou_id}
                onChange={(e) => handleFormChange('cou_id', e.target.value)}
                disabled={dialogType === 'view' || dialogType === 'edit'}
                error={!!formErrors.cou_id}
                helperText={formErrors.cou_id}
                InputProps={{
                  readOnly: dialogType === 'view',
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Course Name"
                value={formData.cou_name}
                onChange={(e) => handleFormChange('cou_name', e.target.value)}
                disabled={dialogType === 'view'}
                error={!!formErrors.cou_name}
                helperText={formErrors.cou_name || "Enter the full name of the course"}
                InputProps={{
                  readOnly: dialogType === 'view',
                }}
                multiline
                rows={2}
              />
            </Grid>

            {dialogType === 'view' && currentCourse && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Branch ID"
                    value={currentCourse.branch_id}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Business ID"
                    value={currentCourse.business_id}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {currentCourse.created_at && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Created At"
                      value={new Date(currentCourse.created_at).toLocaleString()}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={handleCloseDialog} color="inherit">
            {dialogType === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogType !== 'view' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#4f46e5',
                '&:hover': { bgcolor: '#4338ca' }
              }}
            >
              {loading ? 'Processing...' : dialogType === 'create' ? 'Create Course' : 'Update Course'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
      </IonContent>
    </IonPage>
   
  );
};

export default CbtCourses;